import logger from '../../utils/logger';
import { getProgramStatusColor, getCategoryBadgeColor, getRsvpTypeColor, getPaymentStatusColor, getRsvpStatusColor, getVolunteerStatusColor, getCheckinColor } from '../../utils/statusColors';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminEventsTab from './tabs/AdminEventsTab';
import AdminSettingsAccountTab from './tabs/AdminSettingsAccountTab';
import AdminDataCleanup from './tabs/AdminDataCleanup';
import AdminContentManager from './tabs/AdminContentManager';
import AdminDonationsTab from './tabs/AdminDonationsTab';
import InKindDonationsTab from './tabs/InKindDonationsTab';
import AdminEmailScanTab from './tabs/AdminEmailScanTab';
import AdminPageContentTab from './tabs/AdminPageContentTab';
import AdminImpactCounterTab from './tabs/AdminImpactCounterTab';
import AdminUsersTab from './tabs/AdminUsersTab';
import AdminActivityLogTab from './tabs/AdminActivityLogTab';
import AdminProfileTab from './tabs/AdminProfileTab';
import AdminSessionsTab from './tabs/AdminSessionsTab';
import AdminTwoFactorTab from './tabs/AdminTwoFactorTab';
import AdminWebhookHealthTab from './tabs/AdminWebhookHealthTab';
import AdminLiveOpsTab from './tabs/AdminLiveOpsTab';
import AdminSuggestionsTab from './tabs/AdminSuggestionsTab';
import AdminOutreachTab from './tabs/AdminOutreachTab';
import AdminReconciliationTab from './tabs/AdminReconciliationTab';
import AdminDonorStatementsTab from './tabs/AdminDonorStatementsTab';
import AdminGalleryTab from './tabs/AdminGalleryTab';
import AdminShopTab from './tabs/AdminShopTab';
import AdminPopupTab from './tabs/AdminPopupTab';
import { TestimonialsTab, UpdatesTab } from './tabs/AdminContentTabs';
import { SiteImagesTab, AppealTab, VolunteersTab, ReceiptsTab, AllocationTab } from './tabs/AdminSettingsTabs';
import { AdminProgramsTab, ProgramDonationsTab } from './tabs/AdminProgramsTab';
import CampaignManagerTab from './tabs/CampaignManagerTab';
import { DashboardOverviewTab, DonationsTab, GoalsTab, ContactsTab, SubscribersTab, CountriesTab } from './tabs/AdminOverviewTabs';
import AdminQrCodesTab from './tabs/AdminQrCodesTab';
import NotificationsBell from '../../components/admin/NotificationsBell';
import BackupsPanel from '../../components/admin/BackupsPanel';
import GoogleSyncPanel from '../../components/admin/GoogleSyncPanel';
import MediaLibraryPanel from '../../components/admin/MediaLibraryPanel';
import SectionsConfigPanel from '../../components/admin/SectionsConfigPanel';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  DollarSign, 
  Target, 
  MessageSquare, 
  Users, 
  Globe, 
  LogOut,
  Menu,
  X,
  Mail,
  ChevronRight,
  Loader2,
  QrCode,
  Download,
  MapPin,
  Quote,
  Newspaper,
  CalendarDays,
  Camera,
  ShoppingBag,
  HandHeart,
  FileText,
  AlertTriangle,
  ImageIcon,
  Settings,
  HelpCircle,
  GraduationCap,
  Info,
  Shield,
  Activity,
  User,
  ClipboardList,
  TrendingUp,
  Database,
  Cloud,
  Sliders
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_ALLOC_FOOD_PCT = 60;
const DEFAULT_ALLOC_HYGIENE_PCT = 30;
const DEFAULT_ALLOC_DISTRIBUTION_PCT = 10;

const AdminDashboard = () => {
  const { user, logout, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(() => {
    return localStorage.getItem('novena_admin_guide_seen') !== 'true';
  });
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);
  const [donations, setDonations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Goal editing state
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalForm, setGoalForm] = useState({ goal_amount: 5000, title: '', description: '' });
  
  // Country editing state
  const [editingCountry, setEditingCountry] = useState(null);
  const [countryForm, setCountryForm] = useState({ name: '', region: '', description: '', is_primary: false, status: 'planned' });

  // Country Programs state
  const [countryPrograms, setCountryPrograms] = useState([]);
  const [editingProgram, setEditingProgram] = useState(null);
  const [programDonations, setProgramDonations] = useState([]);

  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({ name: '', role: '', quote: '', country: '', is_featured: true });

  // Updates state
  const [updates, setUpdates] = useState([]);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [updateForm, setUpdateForm] = useState({
    // Discriminator — spec: reuse Admin → Updates for distributions.
    // "community" = regular community update; "distribution" = physical
    // distribution record with quantity/location/partner + status.
    type: 'community',
    title: '', description: '', category: 'Community Support',
    image_url: '', date: '', is_published: true,
    // Distribution-only fields (ignored when type !== 'distribution')
    status: 'completed', item_type: 'Hygiene Kits', quantity: 0,
    people_served: 0, location: '', partner: '',
    additional_photos: [],
    // Independent of is_published — controls counting toward impact
    // totals only. Defaults to true so newly-created completed
    // distributions count automatically.
    include_in_totals: true,
  });

  // Events state
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '', date: '', time: '', location: '', description: '', flyer_image: '',
    photos: [], ticket_price: 0, ticket_type: 'free', rsvp_enabled: true,
    donation_items: [], confirmation_message: 'Thank you for registering! We look forward to seeing you.',
    is_active: false, is_published: false, status: 'upcoming',
    recap_text: '', funds_raised: 0, candles_lit: 0, total_attendees: 0,
    donation_items_collected: 0, event_photos: [],
    max_capacity: 0, vip_price: 0, donation_rsvp_enabled: false, vip_rsvp_enabled: false,
    fundraising: { enabled: false, goal_amount: 0, current_amount: 0, title: 'Help Us Reach Our Goal', support_text: '', cta_text: 'Donate Now', cta_link: '', start_date: '', end_date: '', visible_after_event: true, rollover: 'event_only' },
    event_items: [],
    suggested_items: { enabled: false, show_on_rsvp: true, header_text: 'Guests are encouraged, but not required, to bring an item', items: [] }
  });
  const [eventAttendees, setEventAttendees] = useState([]);
  const [viewingEventId, setViewingEventId] = useState(null);
  const [completingEventId, setCompletingEventId] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    recap_text: '', funds_raised: 0, candles_lit: 0, total_attendees: 0,
    donation_items_collected: 0, event_photos: []
  });
  const [uploadingFor, setUploadingFor] = useState(null);
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const [attendeeEventFilter, setAttendeeEventFilter] = useState('');
  const [allAttendees, setAllAttendees] = useState([]);
  const [showAllAttendees, setShowAllAttendees] = useState(false);

  // Site Images state
  const [siteImages, setSiteImages] = useState([]);
  const [siteImagePositions, setSiteImagePositions] = useState([]);
  const [uploadingSiteImage, setUploadingSiteImage] = useState(null);

  // Appeal Banner state
  const [appealForm, setAppealForm] = useState({ title: '', message: '', goal_amount: 0, is_active: false, cta_text: 'Donate Now' });
  const [appealLoading, setAppealLoading] = useState(false);

  // Volunteers state
  const [volunteers, setVolunteers] = useState([]);
  const [editingVolunteer, setEditingVolunteer] = useState(null);
  const [volunteerFilter, setVolunteerFilter] = useState('all');

  // Tax Receipts state
  const [receipts, setReceipts] = useState([]);

  // Fund Allocation state
  const [allocSettings, setAllocSettings] = useState({
    mode: 'auto',
    manual_buckets: [
      { key: 'food_supplies', label: 'Food & supplies', percentage: DEFAULT_ALLOC_FOOD_PCT, amount: 0, color: '#BA1826' },
      { key: 'hygiene_essentials', label: 'Hygiene essentials', percentage: DEFAULT_ALLOC_HYGIENE_PCT, amount: 0, color: '#FCA5A5' },
      { key: 'distribution', label: 'Distribution', percentage: DEFAULT_ALLOC_DISTRIBUTION_PCT, amount: 0, color: '#FECACA' },
    ],
    category_map: {}
  });
  const [allocSaving, setAllocSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    } else if (!authLoading && user && !isAdmin) {
      navigate('/');
    }
  }, [user, isAdmin, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, donationsRes, contactsRes, subscribersRes, countriesRes, programsRes, testimonialsRes, updatesRes, eventsRes, siteImagesRes, appealRes, volunteersRes, receiptsRes, allocRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/donations`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/contacts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/subscribers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/countries`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/country-programs`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/testimonials`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/updates`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/events`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/site-images`, { withCredentials: true }).catch(() => ({ data: { images: [], positions: [] } })),
        axios.get(`${BACKEND_URL}/api/admin/appeal`, { withCredentials: true }).catch(() => ({ data: { title: '', message: '', goal_amount: 0, is_active: false, cta_text: 'Donate Now' } })),
        axios.get(`${BACKEND_URL}/api/admin/volunteers`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/receipts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/fund-allocation`, { withCredentials: true }).catch(() => ({ data: { mode: 'auto', manual_buckets: [], category_map: {} } }))
      ]);
      
      setDashboardData(dashboardRes.data);
      setDonations(donationsRes.data);
      setContacts(contactsRes.data);
      setSubscribers(subscribersRes.data);
      setCountries(countriesRes.data);
      setCountryPrograms(programsRes.data);
      setTestimonials(testimonialsRes.data);
      setUpdates(updatesRes.data);
      setEvents(eventsRes.data);
      setSiteImages(siteImagesRes.data.images || []);
      setSiteImagePositions(siteImagesRes.data.positions || []);
      setAppealForm(appealRes.data);
      setVolunteers(volunteersRes.data);
      setReceipts(receiptsRes.data);
      if (allocRes.data) setAllocSettings(allocRes.data);
      
      if (dashboardRes.data.fundraising_goal) {
        setGoalForm({
          goal_amount: dashboardRes.data.fundraising_goal.goal_amount,
          title: dashboardRes.data.fundraising_goal.title,
          description: dashboardRes.data.fundraising_goal.description
        });
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && isAdmin) {
      fetchData();
    }
  }, [user, isAdmin, fetchData]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/admin/login');
  }, [logout, navigate]);

  const handleSiteImageUpload = useCallback(async (key, file) => {
    setUploadingSiteImage(key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${BACKEND_URL}/api/admin/site-images/${key}`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      fetchData();
    } catch (e) { alert('Upload failed: ' + (e.response?.data?.detail || 'Unknown error')); }
    setUploadingSiteImage(null);
  }, [fetchData]);

  const handleDeleteSiteImage = useCallback(async (key) => {
    if (!window.confirm('Remove this site image? The placeholder will be shown instead.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/site-images/${key}`, { withCredentials: true });
      fetchData();
    } catch (e) { logger.error(e); }
  }, [fetchData]);

  const saveAppeal = useCallback(async () => {
    setAppealLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/appeal`, appealForm, { withCredentials: true });
      fetchData();
    } catch (e) { alert('Failed to save appeal: ' + (e.response?.data?.detail || 'Unknown error')); }
    setAppealLoading(false);
  }, [appealForm, fetchData]);

  const updateVolunteer = useCallback(async (volId, data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/volunteers/${volId}`, data, { withCredentials: true });
      fetchData();
      setEditingVolunteer(null);
    } catch (e) { alert('Update failed'); }
  }, [fetchData]);

  const deleteVolunteer = useCallback(async (volId) => {
    if (!window.confirm('Delete this volunteer submission?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/volunteers/${volId}`, { withCredentials: true });
      fetchData();
    } catch (e) { logger.error(e); }
  }, [fetchData]);

  const saveAllocSettings = useCallback(async () => {
    setAllocSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/fund-allocation`, allocSettings, { withCredentials: true });
      fetchData();
    } catch (e) { alert('Failed to save: ' + (e.response?.data?.detail || 'Unknown error')); }
    setAllocSaving(false);
  }, [allocSettings, fetchData]);

  const updateManualPct = useCallback((key, value) => {
    setAllocSettings(prev => ({
      ...prev,
      manual_buckets: prev.manual_buckets.map(b =>
        b.key === key ? { ...b, percentage: Math.max(0, Math.min(100, parseInt(value) || 0)) } : b
      )
    }));
  }, []);

  const updateGoal = useCallback(async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/fundraising-goal`, goalForm, { withCredentials: true });
      setEditingGoal(false);
      fetchData();
    } catch (error) {
      logger.error('Error updating goal:', error);
    }
  }, [goalForm, fetchData]);

  const markContactRead = useCallback(async (contactId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/contacts/${contactId}/status`, { status: 'read' }, { withCredentials: true });
      fetchData();
    } catch (error) {
      logger.error('Error updating contact:', error);
    }
  }, [fetchData]);

  const addCountry = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/countries`, countryForm, { withCredentials: true });
      setCountryForm({ name: '', region: '', description: '', is_primary: false, status: 'planned' });
      fetchData();
    } catch (error) {
      logger.error('Error adding country:', error);
    }
  }, [countryForm, fetchData]);

  const updateCountry = useCallback(async (countryId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/countries/${countryId}`, countryForm, { withCredentials: true });
      setEditingCountry(null);
      fetchData();
    } catch (error) {
      logger.error('Error updating country:', error);
    }
  }, [countryForm, fetchData]);

  const deleteCountry = useCallback(async (countryId) => {
    if (!window.confirm('Are you sure you want to delete this country?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/countries/${countryId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      logger.error('Error deleting country:', error);
    }
  }, [fetchData]);

  const updateProgram = useCallback(async (programId, data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/country-programs/${programId}`, data, { withCredentials: true });
      setEditingProgram(null);
      fetchData();
    } catch (error) {
      logger.error('Error updating program:', error);
    }
  }, [fetchData]);

  const createProgram = useCallback(async (data) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/country-programs`,
        data, { withCredentials: true });
      await fetchData();
      return res.data;
    } catch (error) {
      logger.error('Error creating program:', error);
      alert(error.response?.data?.detail || 'Failed to create program.');
      throw error;
    }
  }, [fetchData]);

  const deleteProgram = useCallback(async (programId, label) => {
    if (!window.confirm(
      `Delete the "${label}" country program? Its donation history is preserved but the program page will 404.`
    )) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/country-programs/${programId}`,
        { withCredentials: true });
      fetchData();
    } catch (error) {
      logger.error('Error deleting program:', error);
      alert('Failed to delete program.');
    }
  }, [fetchData]);

  const fetchProgramDonations = useCallback(async (programId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/country-programs/${programId}/donations`, { withCredentials: true });
      setProgramDonations(res.data);
    } catch (error) {
      logger.error('Error fetching program donations:', error);
    }
  }, []);

  const saveTestimonial = useCallback(async () => {
    try {
      if (editingTestimonial) {
        await axios.put(`${BACKEND_URL}/api/admin/testimonials/${editingTestimonial.id}`, testimonialForm, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/testimonials`, testimonialForm, { withCredentials: true });
      }
      setEditingTestimonial(null);
      setTestimonialForm({ name: '', role: '', quote: '', country: '', is_featured: true });
      fetchData();
    } catch (error) {
      logger.error('Error saving testimonial:', error);
    }
  }, [editingTestimonial, testimonialForm, fetchData]);

  const deleteTestimonial = useCallback(async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/testimonials/${id}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      logger.error('Error deleting testimonial:', error);
    }
  }, [fetchData]);

  const saveUpdate = useCallback(async () => {
    try {
      const payload = { ...updateForm };
      if (!payload.date) {
        payload.date = new Date().toISOString().split('T')[0];
      }
      // Only distributions send the extra fields to the backend; the
      // sanitiser there will strip them from non-distribution rows,
      // but sending the minimal payload keeps the request clean.
      if (payload.type === 'distribution') {
        payload.type = 'distribution';
        payload.quantity = Number(payload.quantity) || 0;
        payload.people_served = Number(payload.people_served) || 0;
      } else {
        delete payload.type; // regular community update: no discriminator
        delete payload.status;
        delete payload.item_type;
        delete payload.quantity;
        delete payload.people_served;
        delete payload.location;
        delete payload.partner;
        delete payload.additional_photos;
        delete payload.include_in_totals;
      }
      if (editingUpdate) {
        await axios.put(`${BACKEND_URL}/api/admin/updates/${editingUpdate.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/updates`, payload, { withCredentials: true });
      }
      setEditingUpdate(null);
      setUpdateForm({
        type: 'community', title: '', description: '',
        category: 'Community Support', image_url: '', date: '', is_published: true,
        status: 'completed', item_type: 'Hygiene Kits', quantity: 0,
        people_served: 0, location: '', partner: '', additional_photos: [],
        include_in_totals: true,
      });
      fetchData();
    } catch (error) {
      logger.error('Error saving update:', error);
      alert('Save failed: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  }, [updateForm, editingUpdate, fetchData]);

  const deleteUpdate = useCallback(async (id) => {
    if (!window.confirm('Delete this update?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/updates/${id}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      logger.error('Error deleting update:', error);
    }
  }, [fetchData]);

  const resetEventForm = useCallback(() => {
    setEventForm({
      title: '', date: '', time: '', location: '', description: '', flyer_image: '',
      photos: [], ticket_price: 0, ticket_type: 'free', rsvp_enabled: true,
      donation_items: [], confirmation_message: 'Thank you for registering! We look forward to seeing you.',
      is_active: false, is_published: false, status: 'upcoming',
      recap_text: '', funds_raised: 0, candles_lit: 0, total_attendees: 0,
      donation_items_collected: 0, event_photos: [],
      max_capacity: 0, vip_price: 0, donation_rsvp_enabled: false, vip_rsvp_enabled: false,
      donation_presets: [10, 25, 50, 100, 250, 500], donation_custom_enabled: true,
      fundraising: { enabled: false, goal_amount: 0, current_amount: 0, title: 'Help Us Reach Our Goal', support_text: '', cta_text: 'Donate Now', cta_link: '', start_date: '', end_date: '', visible_after_event: true, rollover: 'event_only' },
      event_items: [],
      suggested_items: { enabled: false, show_on_rsvp: true, header_text: 'Guests are encouraged, but not required, to bring an item', items: [] }
    });
    setEditingEvent(null);
  }, []);

  const saveEvent = useCallback(async () => {
    try {
      const payload = { ...eventForm, ticket_price: parseFloat(eventForm.ticket_price) || 0 };
      if (!editingEvent && (!payload.donation_items || payload.donation_items.length === 0)) {
        delete payload.donation_items;
      }
      if (editingEvent) {
        await axios.put(`${BACKEND_URL}/api/admin/events/${editingEvent.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/events`, payload, { withCredentials: true });
      }
      resetEventForm();
      fetchData();
    } catch (error) {
      logger.error('Error saving event:', error);
      // Surface backend validation errors (e.g., Universal Media
      // Standard 12-total cap) so the admin knows why the save failed.
      const detail = error?.response?.data?.detail;
      if (detail) {
        window.alert(typeof detail === 'string' ? detail : JSON.stringify(detail));
      } else {
        window.alert('Failed to save event. Please try again.');
      }
    }
  }, [eventForm, editingEvent, resetEventForm, fetchData]);

  const deleteEvent = useCallback(async (id) => {
    if (!window.confirm('Delete this event and all its attendees?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/events/${id}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      logger.error('Error deleting event:', error);
    }
  }, [fetchData]);

  const toggleEventActive = useCallback(async (ev) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/events/${ev.id}`, {
        is_active: !ev.is_active, is_published: !ev.is_active ? true : ev.is_published
      }, { withCredentials: true });
      fetchData();
    } catch (error) {
      logger.error('Error toggling event:', error);
    }
  }, [fetchData]);

  const loadAttendees = useCallback(async (eventId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/events/${eventId}/attendees`, { withCredentials: true });
      setEventAttendees(res.data);
      setViewingEventId(eventId);
    } catch (error) {
      logger.error('Error loading attendees:', error);
    }
  }, []);

  const toggleCheckIn = useCallback(async (attendee) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/events/attendees/${attendee.id}`, {
        checked_in: !attendee.checked_in
      }, { withCredentials: true });
      loadAttendees(attendee.event_id);
    } catch (error) {
      logger.error('Error updating attendee:', error);
    }
  }, [loadAttendees]);

  const handleFileUpload = useCallback(async (e, target) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingFor(target);
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data.url;
      if (target === 'flyer') {
        setEventForm(prev => ({ ...prev, flyer_image: url }));
      } else if (target === 'event_photos') {
        setEventForm(prev => ({ ...prev, event_photos: [...prev.event_photos, url] }));
      } else if (target === 'complete_photos') {
        setCompleteForm(prev => ({ ...prev, event_photos: [...prev.event_photos, url] }));
      }
    } catch (error) {
      logger.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingFor(null);
      e.target.value = '';
    }
  }, []);

  const startCompleteEvent = useCallback((ev) => {
    setCompletingEventId(ev.id);
    setCompleteForm({
      recap_text: ev.recap_text || '',
      funds_raised: ev.funds_raised || 0,
      candles_lit: ev.candles_lit || 0,
      total_attendees: ev.total_attendees || 0,
      donation_items_collected: ev.donation_items_collected || 0,
      event_photos: ev.event_photos || []
    });
  }, []);

  const submitCompleteEvent = useCallback(async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/events/${completingEventId}/complete`, completeForm, { withCredentials: true });
      setCompletingEventId(null);
      fetchData();
    } catch (error) {
      logger.error('Error completing event:', error);
    }
  }, [completingEventId, completeForm, fetchData]);

  const loadAllAttendees = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/events/attendees/all`, { withCredentials: true });
      setAllAttendees(res.data);
      setShowAllAttendees(true);
    } catch (error) {
      logger.error('Error loading all attendees:', error);
    }
  }, []);

  const exportAttendeesCsv = useCallback(async (eventId = '') => {
    try {
      const url = eventId 
        ? `${BACKEND_URL}/api/admin/events/attendees/export?event_id=${eventId}`
        : `${BACKEND_URL}/api/admin/events/attendees/export`;
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = eventId ? `attendees_${eventId}.csv` : 'all_attendees.csv';
      link.click();
    } catch (error) {
      logger.error('Error exporting CSV:', error);
    }
  }, []);

  const sendReminders = useCallback(async (eventId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/events/${eventId}/send-reminders`, {}, { withCredentials: true });
      alert(res.data.message);
    } catch (error) {
      logger.error('Error sending reminders:', error);
    }
  }, []);

  // Role-based tab access mapping
  const TAB_ROLES = useMemo(() => ({
    dashboard: ['super_admin', 'content_admin', 'finance_admin', 'events_admin', 'viewer'],
    donations: ['super_admin', 'finance_admin', 'viewer'],
    in_kind_donations: ['super_admin', 'finance_admin', 'viewer'],
    goals: ['super_admin'],
    programs: ['super_admin', 'content_admin', 'viewer'],
    campaigns: ['super_admin', 'content_admin', 'finance_admin'],
    testimonials: ['super_admin', 'content_admin', 'viewer'],
    updates: ['super_admin', 'content_admin', 'viewer'],
    events: ['super_admin', 'events_admin', 'viewer'],
    volunteers: ['super_admin', 'content_admin', 'viewer'],
    receipts: ['super_admin', 'finance_admin', 'viewer'],
    allocation: ['super_admin', 'finance_admin'],
    contacts: ['super_admin', 'content_admin', 'viewer'],
    subscribers: ['super_admin', 'content_admin', 'viewer'],
    countries: ['super_admin', 'content_admin', 'viewer'],
    qrcode: ['super_admin', 'content_admin', 'events_admin'],
    site_images: ['super_admin', 'content_admin'],
    appeal: ['super_admin', 'content_admin'],
    settings: ['super_admin'],
    content_manager: ['super_admin'],
    email_scan: ['super_admin'],
    page_content: ['super_admin', 'content_admin'],
    impact_counter: ['super_admin', 'content_admin'],
    donor_statements: ['super_admin', 'finance_admin'],
    galleries: ['super_admin', 'events_admin', 'content_admin'],
    shop: ['super_admin', 'events_admin'],
    popup: ['super_admin', 'events_admin'],
    live_ops: ['super_admin', 'events_admin', 'finance_admin'],
    suggestions: ['super_admin', 'content_admin', 'events_admin', 'finance_admin', 'viewer'],
    outreach: ['super_admin', 'content_admin', 'events_admin', 'finance_admin', 'viewer'],
    finance: ['super_admin', 'finance_admin'],
    users: ['super_admin'],
    activity_log: ['super_admin'],
    backups: ['super_admin'],
    google_sync: ['super_admin'],
    media_library: ['super_admin', 'content_admin', 'outreach_admin', 'events_admin'],
    section_config: ['super_admin', 'content_admin', 'outreach_admin', 'events_admin'],
    profile: ['super_admin', 'content_admin', 'finance_admin', 'events_admin', 'viewer'],
    sessions: ['super_admin'],
    two_factor: ['super_admin', 'content_admin', 'finance_admin', 'events_admin', 'viewer'],
    webhook_health: ['super_admin', 'finance_admin'],
    payment_reconciliation: ['super_admin', 'finance_admin'],
  }), []);

  const userRole = user?.role === 'admin' ? 'super_admin' : (user?.role || 'viewer');
  const canAccessTab = useCallback((tabId) => (TAB_ROLES[tabId] || []).includes(userRole), [TAB_ROLES, userRole]);

  const allNavItems = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, desc: 'Your overview — see total donations, messages, subscribers, and fundraising progress at a glance.' },
    { id: 'donations', label: 'Donations', icon: DollarSign, desc: 'View all donations received, including donor names, amounts, and payment status.' },
    { id: 'in_kind_donations', label: 'In-Kind Donations', icon: DollarSign, desc: 'Record physical item contributions (wheelchairs, walkers, hygiene kits, clothing) and generate noncash tax acknowledgments.' },
    { id: 'goals', label: 'Fundraising Goals', icon: Target, desc: 'Set and manage your fundraising goal amount.' },
    { id: 'appeal', label: 'Appeal Banner', icon: AlertTriangle, desc: 'Create an urgent appeal banner that appears across your website.' },
    { id: 'programs', label: 'Country Programs', icon: MapPin, desc: 'Manage each country program — edit descriptions, update bucket costs.' },
    { id: 'campaigns', label: 'Campaign Manager', icon: Target, desc: 'One place for every fundraising campaign — crisis response, fundraisers, supply drives. Each gets its own donation link and QR code so donations are tracked separately. Includes PDF/CSV transparency reports, distributions log, and donor emails.' },
    { id: 'testimonials', label: 'Testimonials', icon: Quote, desc: 'Review and approve stories submitted by visitors.' },
    { id: 'updates', label: 'Updates', icon: Newspaper, desc: 'Post community updates that appear on the Updates page.' },
    { id: 'events', label: 'Events', icon: CalendarDays, desc: 'Create and manage events with RSVP tracking and QR codes.' },
    { id: 'galleries', label: 'Photo Galleries', icon: Camera, desc: 'Create event photo galleries with QR codes for guest uploads.' },
    { id: 'shop', label: 'Shop & Merch', icon: ShoppingBag, desc: 'Manage merch items, track sales, and generate QR codes.' },
    { id: 'popup', label: 'Pop-Up Shops', icon: QrCode, desc: 'Create in-person QR-code pop-up shops with Apple Pay / Google Pay.' },
    { id: 'live_ops', label: 'Live Ops', icon: Activity, desc: 'Live Event Operations Mode — today metrics, real-time sales feed, low-stock alerts, pop-up conversion, and offline sale entry.' },
    { id: 'suggestions', label: 'Suggestions & Feedback', icon: Activity, desc: 'Community ideas submitted via the homepage — review, status changes, internal notes, and CSV export.' },
    { id: 'outreach', label: 'Community Outreach', icon: HandHeart, desc: 'Door-to-door relationships — zones, streets, conversations, follow-ups, surveys. Donations are one outcome of many.' },
    { id: 'volunteers', label: 'Volunteers', icon: HandHeart, desc: 'View and manage volunteer sign-ups.' },
    { id: 'receipts', label: 'Tax Receipts', icon: FileText, desc: 'Generate and manage tax receipts for donors.' },
    { id: 'donor_statements', label: 'Donor Statements', icon: FileText, desc: 'Generate year-end giving summaries for donors.' },
    { id: 'allocation', label: 'Fund Allocation', icon: Target, desc: 'Control how donated funds are distributed.' },
    { id: 'contacts', label: 'Contact Messages', icon: MessageSquare, desc: 'Read messages from the Contact Us form.' },
    { id: 'subscribers', label: 'Newsletter', icon: Mail, desc: 'View newsletter subscribers.' },
    { id: 'countries', label: 'International', icon: Globe, desc: 'Manage the countries where your foundation operates.' },
    { id: 'qrcode', label: 'QR Code', icon: QrCode, desc: 'Generate branded QR codes for donations and events.' },
    { id: 'site_images', label: 'Site Images', icon: ImageIcon, desc: 'Upload and manage website images.' },
    { id: 'finance', label: 'Finance & Docs', icon: DollarSign, isLink: true, desc: 'Track expenses, reimbursements, trips, and reports.' },
    { id: 'payment_reconciliation', label: 'Payment Reconciliation', icon: Shield, desc: 'Net confirmed revenue, payment-status breakdown, Stripe reconciliation, CSV export, and suspicious-activity alerts.' },
    { id: 'webhook_health', label: 'Webhook Hardening', icon: Shield, desc: 'Verify the live Stripe webhook endpoint has every event type the reconciliation logic expects. One-click sync available.' },
    { id: 'divider_admin', isDivider: true, label: 'ADMIN' },
    { id: 'users', label: 'User Management', icon: Shield, desc: 'Create, edit, and deactivate admin users. Assign roles.' },
    { id: 'sessions', label: 'Active Sessions', icon: Shield, desc: 'Super-admin override — view and revoke any admin session across the platform.' },
    { id: 'two_factor', label: 'Two-Factor Auth', icon: Shield, desc: 'Enable 2FA on your account, manage backup codes, and (super admin) enforce 2FA org-wide.' },
    { id: 'activity_log', label: 'Activity Log', icon: Activity, desc: 'Searchable log of all admin actions.' },
    { id: 'backups', label: 'Backups & Recovery', icon: Database, desc: 'Automated nightly snapshots of all operational collections. Download archives or restore from a specific date.' },
    { id: 'google_sync', label: 'Google Drive & Sheets', icon: Cloud, desc: 'Mirror partnership documents to Google Drive and refresh a master spreadsheet of organizations, contacts, interactions, and opportunities each morning. Off-pod backup copies live in Drive too.' },
    { id: 'media_library', label: 'Media Library', icon: ImageIcon, desc: 'Centralized library for every image (and later video) used across the site. Auto-optimizes into responsive variants, organizes by album, mirrors to Google Drive when configured.' },
    { id: 'section_config', label: 'Sections & Slideshows', icon: Sliders, desc: 'Configure how each region of the public site displays media. Toggle single-image vs slideshow, reorder images, pick a cover, choose transitions, autoplay speed.' },
    { id: 'profile', label: 'My Profile', icon: User, desc: 'View your profile, change password, see permissions.' },
    { id: 'settings', label: 'Settings', icon: Settings, desc: 'Change admin email and display name.' },
    { id: 'content_manager', label: 'Content Manager', icon: ClipboardList, desc: 'View and remove content from any section. Find and delete test data.' },
    { id: 'email_scan', label: 'Email Validation', icon: Mail, desc: 'Scan for invalid emails, fix or exclude bad addresses.' },
    { id: 'page_content', label: 'Page Content', icon: FileText, desc: 'Edit cost breakdowns, text, and visibility for public pages.' },
    { id: 'impact_counter', label: 'Impact Counter', icon: TrendingUp, desc: 'Manage the 4 live stats on the homepage (Families, Donated, Buckets, Hygiene Kits).' },
  ], []);

  const navItems = useMemo(() => allNavItems.filter(item => item.isDivider || canAccessTab(item.id)), [allNavItems, canAccessTab]);

  const pendingTestimonials = useMemo(() => testimonials.filter(t => t.status === 'pending'), [testimonials]);
  const approvedTestimonials = useMemo(() => testimonials.filter(t => t.status === 'approved'), [testimonials]);
  const filteredVolunteers = useMemo(() => volunteers.filter(v => volunteerFilter === 'all' || v.status === volunteerFilter), [volunteers, volunteerFilter]);

  // ==================== Memoized ctx objects to prevent re-renders ====================

  const goalsCtx = useMemo(() => ({
    dashboardData, editingGoal, setEditingGoal, goalForm, setGoalForm, updateGoal
  }), [dashboardData, editingGoal, goalForm, updateGoal]);

  const countriesCtx = useMemo(() => ({
    countries, countryForm, setCountryForm, addCountry, deleteCountry
  }), [countries, countryForm, addCountry, deleteCountry]);

  const programsCtx = useMemo(() => ({
    countryPrograms, editingProgram, setEditingProgram, updateProgram,
    createProgram, deleteProgram, fetchProgramDonations, setActiveTab
  }), [countryPrograms, editingProgram, updateProgram, createProgram, deleteProgram, fetchProgramDonations]);

  const testimonialsCtx = useMemo(() => ({
    testimonials, testimonialForm, setTestimonialForm, editingTestimonial, setEditingTestimonial,
    pendingTestimonials, approvedTestimonials, saveTestimonial, deleteTestimonial, fetchData
  }), [testimonials, testimonialForm, editingTestimonial, pendingTestimonials, approvedTestimonials, saveTestimonial, deleteTestimonial, fetchData]);

  const siteImagesCtx = useMemo(() => ({
    siteImages, siteImagePositions, uploadingSiteImage, handleSiteImageUpload, handleDeleteSiteImage
  }), [siteImages, siteImagePositions, uploadingSiteImage, handleSiteImageUpload, handleDeleteSiteImage]);

  const updatesCtx = useMemo(() => ({
    updates, updateForm, setUpdateForm, editingUpdate, setEditingUpdate, saveUpdate, deleteUpdate
  }), [updates, updateForm, editingUpdate, saveUpdate, deleteUpdate]);

  const eventsFormCtx = useMemo(() => ({
    events, eventForm, setEventForm, editingEvent, setEditingEvent, resetEventForm,
    saveEvent, deleteEvent, toggleEventActive, fetchData
  }), [events, eventForm, editingEvent, saveEvent, deleteEvent, toggleEventActive, resetEventForm, fetchData]);

  const eventsAttendeeCtx = useMemo(() => ({
    eventAttendees, viewingEventId, setViewingEventId,
    allAttendees, showAllAttendees,
    attendeeSearch, setAttendeeSearch, attendeeEventFilter, setAttendeeEventFilter,
    loadAttendees, toggleCheckIn, loadAllAttendees, exportAttendeesCsv, sendReminders
  }), [eventAttendees, viewingEventId, allAttendees, showAllAttendees,
    attendeeSearch, attendeeEventFilter, loadAttendees, toggleCheckIn,
    loadAllAttendees, exportAttendeesCsv, sendReminders]);

  const eventsCompleteCtx = useMemo(() => ({
    completingEventId, setCompletingEventId, completeForm, setCompleteForm,
    uploadingFor,
    handleFileUpload, startCompleteEvent, submitCompleteEvent
  }), [completingEventId, completeForm, uploadingFor,
    handleFileUpload, startCompleteEvent, submitCompleteEvent]);

  const eventsCtx = useMemo(() => ({
    ...eventsFormCtx, ...eventsAttendeeCtx, ...eventsCompleteCtx
  }), [eventsFormCtx, eventsAttendeeCtx, eventsCompleteCtx]);

  const appealCtx = useMemo(() => ({
    appealForm, setAppealForm, saveAppeal, appealLoading
  }), [appealForm, saveAppeal, appealLoading]);

  const volunteersCtx = useMemo(() => ({
    volunteers, filteredVolunteers, volunteerFilter, setVolunteerFilter,
    editingVolunteer, setEditingVolunteer, setVolunteers, updateVolunteer, deleteVolunteer
  }), [volunteers, filteredVolunteers, volunteerFilter, editingVolunteer, updateVolunteer, deleteVolunteer]);

  const allocCtx = useMemo(() => ({
    allocSettings, setAllocSettings, allocSaving, saveAllocSettings, updateManualPct
  }), [allocSettings, allocSaving, saveAllocSettings, updateManualPct]);

  const programDonationsCtx = useMemo(() => ({
    activeTab, countryPrograms, programDonations, setActiveTab
  }), [activeTab, countryPrograms, programDonations]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 flex flex-col h-screen ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-800 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_humanitarian-aid-7/artifacts/5fygylhw_IMG_0281.jpeg"
              alt="Logo"
              className="w-8 h-8"
            />
            <span className="font-heading font-bold text-white">Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const next = !showGuide;
                setShowGuide(next);
                localStorage.setItem('novena_admin_guide_seen', next ? 'false' : 'true');
              }}
              className={`p-1.5 rounded-lg transition-colors ${showGuide ? 'bg-[#BA1826] text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
              title={showGuide ? 'Hide guide descriptions' : 'Show guide descriptions'}
              data-testid="toggle-guide-btn"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            <button onClick={() => setMobileMenuOpen(false)} className="lg:hidden text-gray-400">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <nav className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="admin-sidebar-nav">
          {navItems.map((item) => (
            item.isDivider ? (
              <div key={item.id} className="pt-4 pb-1 px-4">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{item.label}</p>
              </div>
            ) : item.isLink ? (
              <div key={item.id}>
                <Link
                  to="/admin/finance"
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                  data-testid="nav-finance-link"
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                  <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0" />
                </Link>
                {showGuide && (
                  <p className="text-[11px] text-gray-500 px-4 pb-2 leading-relaxed">{item.desc}</p>
                )}
              </div>
            ) : (
              <div key={item.id}>
                <button
                  onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                  data-testid={`admin-${item.id}-tab`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === item.id
                      ? 'bg-[#BA1826] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </button>
                {showGuide && (
                  <p className="text-[11px] text-gray-500 px-4 pb-2 leading-relaxed">{item.desc}</p>
                )}
              </div>
            )
          ))}
        </nav>
        
        <div className="flex-shrink-0 p-4 border-t border-gray-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#BA1826] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-[10px] text-gray-400 truncate">{user?.email}</p>
              <span className="inline-block mt-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">
                {userRole.replace(/_/g, ' ').toUpperCase()}
              </span>
            </div>
          </div>
          <Link
            to="/admin/help"
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="admin-help-link"
          >
            <HelpCircle className="w-4 h-4" />
            Help Center & Training
          </Link>
          <Link
            to="/admin/training"
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="admin-training-dashboard-link"
          >
            <GraduationCap className="w-4 h-4" />
            Training Dashboard
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            data-testid="admin-signout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-[#E5E7EB] px-4 py-4 flex items-center justify-between">
          <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden">
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-xl font-bold text-[#0A0A0A]">
            {navItems.find(n => n.id === activeTab)?.label || 'Dashboard'}
          </h1>
          <div className="flex items-center gap-3">
            <NotificationsBell />
            <Link to="/" className="text-sm text-[#4B5563] hover:text-[#BA1826]">
              View Site →
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 md:p-6">
          {/* Guide Welcome Banner */}
          {showGuide && (
            <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="guide-welcome-banner">
              <Info className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-heading font-bold text-sm text-[#0A0A0A] mb-1">Welcome to your Admin Dashboard</h3>
                <p className="text-xs text-[#4B5563] leading-relaxed">
                  Guide mode is on — each sidebar tab has a description explaining what it does. Click through the tabs to learn your way around. 
                  You can toggle this guide on/off anytime using the <strong>?</strong> button in the sidebar header.
                </p>
              </div>
              <button
                onClick={() => { setShowGuide(false); localStorage.setItem('novena_admin_guide_seen', 'true'); }}
                className="text-xs font-semibold text-[#F59E0B] hover:text-[#D97706] whitespace-nowrap"
                data-testid="dismiss-guide-btn"
              >
                Got it
              </button>
            </div>
          )}

          {/* Tab Description */}
          {showGuide && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="tab-description">
              <HelpCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <p className="text-xs text-blue-700">{navItems.find(n => n.id === activeTab)?.desc}</p>
            </div>
          )}

          {/* Dashboard Overview */}
          {activeTab === 'dashboard' && dashboardData && (
            <DashboardOverviewTab dashboardData={dashboardData} donations={donations} contacts={contacts} subscribers={subscribers} />
          )}

          {/* Donations Tab */}
          {activeTab === 'donations' && (
            <AdminDonationsTab />
          )}

          {/* In-Kind Donations Tab */}
          {activeTab === 'in_kind_donations' && (
            <InKindDonationsTab />
          )}

          {/* Goals Tab */}
          {activeTab === 'goals' && dashboardData?.fundraising_goal && (
            <GoalsTab ctx={goalsCtx} />
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <ContactsTab contacts={contacts} markContactRead={markContactRead} />
          )}

          {/* Subscribers Tab */}
          {activeTab === 'subscribers' && (
            <SubscribersTab subscribers={subscribers} />
          )}

          {/* Countries Tab */}
          {activeTab === 'countries' && (
            <CountriesTab ctx={{ countries, countryForm, setCountryForm, addCountry, deleteCountry }} />
          )}

          {/* Country Programs Tab */}
          {activeTab === 'programs' && (
            <AdminProgramsTab ctx={programsCtx} />
          )}

          {activeTab === 'campaigns' && (
            <CampaignManagerTab countryPrograms={countryPrograms} />
          )}

          {/* Program-specific Donations */}
          {activeTab.startsWith('program_donations_') && (
            <ProgramDonationsTab ctx={programDonationsCtx} />
          )}

          {/* Testimonials Tab */}
          {activeTab === 'testimonials' && (
            <TestimonialsTab ctx={testimonialsCtx} />
          )}

          {/* QR Code Tab */}
          {activeTab === 'qrcode' && (
            <AdminQrCodesTab />
          )}

          {/* Site Images Tab */}
          {activeTab === 'site_images' && (
            <SiteImagesTab ctx={siteImagesCtx} />
          )}

          {/* Updates Tab */}
          {activeTab === 'updates' && (
            <UpdatesTab ctx={updatesCtx} />
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <AdminEventsTab ctx={eventsCtx} />
          )}

          {/* Appeal Banner Tab */}
          {activeTab === 'appeal' && (
            <AppealTab ctx={appealCtx} />
          )}

          {/* Volunteers Tab */}
          {activeTab === 'volunteers' && (
            <VolunteersTab ctx={volunteersCtx} />
          )}

          {/* Tax Receipts Tab */}
          {activeTab === 'receipts' && (
            <ReceiptsTab receipts={receipts} />
          )}

          {/* Donor Statements Tab */}
          {activeTab === 'donor_statements' && canAccessTab('donor_statements') && (
            <AdminDonorStatementsTab />
          )}

          {/* Photo Galleries Tab */}
          {activeTab === 'galleries' && canAccessTab('galleries') && (
            <AdminGalleryTab />
          )}

          {/* Shop Tab */}
          {activeTab === 'shop' && canAccessTab('shop') && (
            <AdminShopTab />
          )}

          {/* Pop-Up Shops Tab */}
          {activeTab === 'popup' && canAccessTab('popup') && (
            <AdminPopupTab />
          )}

          {/* Live Event Operations Mode (Super Admin, Events Admin, Finance Admin) */}
          {activeTab === 'live_ops' && canAccessTab('live_ops') && (
            <AdminLiveOpsTab />
          )}

          {/* Community Suggestions & Feedback */}
          {activeTab === 'suggestions' && canAccessTab('suggestions') && (
            <AdminSuggestionsTab />
          )}

          {/* Community Outreach / Door-to-Door (Phase A) */}
          {activeTab === 'outreach' && canAccessTab('outreach') && (
            <AdminOutreachTab canWrite={['super_admin', 'content_admin', 'events_admin'].includes(userRole)} />
          )}

          {/* Fund Allocation Tab */}
          {activeTab === 'allocation' && (
            <AllocationTab ctx={allocCtx} />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <AdminSettingsAccountTab user={user} onEmailUpdate={(newEmail) => fetchData()} />
              {user?.role === 'super_admin' && <AdminDataCleanup />}
            </div>
          )}

          {/* Content Manager Tab */}
          {activeTab === 'content_manager' && (
            <AdminContentManager />
          )}

          {/* Email Validation Scan Tab */}
          {activeTab === 'email_scan' && (
            <AdminEmailScanTab />
          )}

          {/* Page Content Editor Tab */}
          {activeTab === 'page_content' && (
            <AdminPageContentTab />
          )}

          {/* Impact Counter Tab */}
          {activeTab === 'impact_counter' && canAccessTab('impact_counter') && (
            <AdminImpactCounterTab />
          )}

          {/* User Management Tab (Super Admin only) */}
          {activeTab === 'users' && canAccessTab('users') && (
            <AdminUsersTab />
          )}

          {/* Activity Log Tab (Super Admin only) */}
          {activeTab === 'activity_log' && canAccessTab('activity_log') && (
            <AdminActivityLogTab />
          )}

          {/* Backups & Recovery (Super Admin only) */}
          {activeTab === 'backups' && user?.role === 'super_admin' && (
            <BackupsPanel />
          )}

          {/* Google Drive & Sheets sync (Super Admin only) */}
          {activeTab === 'google_sync' && user?.role === 'super_admin' && (
            <GoogleSyncPanel />
          )}

          {/* Media Library — content admins and above */}
          {activeTab === 'media_library'
            && ['super_admin', 'content_admin', 'outreach_admin', 'events_admin']
                .includes(user?.role) && (
            <MediaLibraryPanel />
          )}

          {/* Section / Slideshow config */}
          {activeTab === 'section_config'
            && ['super_admin', 'content_admin', 'outreach_admin', 'events_admin']
                .includes(user?.role) && (
            <SectionsConfigPanel />
          )}

          {/* Profile Tab (All roles) */}
          {activeTab === 'profile' && (
            <AdminProfileTab />
          )}

          {/* Active Sessions (Super Admin only) */}
          {activeTab === 'sessions' && canAccessTab('sessions') && (
            <AdminSessionsTab />
          )}

          {/* Two-Factor Authentication (All admin roles) */}
          {activeTab === 'two_factor' && canAccessTab('two_factor') && (
            <AdminTwoFactorTab />
          )}

          {/* Payment Reconciliation (Super Admin + Finance Admin) */}
          {activeTab === 'payment_reconciliation' && canAccessTab('payment_reconciliation') && (
            <AdminReconciliationTab />
          )}

          {/* Production Webhook Hardening (Super Admin + Finance Admin) */}
          {activeTab === 'webhook_health' && canAccessTab('webhook_health') && (
            <AdminWebhookHealthTab />
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
